import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DocumentPickerModal } from '@/components/media/DocumentPickerModal'
import {
    interviewCompileService,
    type InterviewCompileCandidate,
    type InterviewCompileJob,
    type InterviewApplyRun,
    type InterviewCompileProfileType,
    type InterviewCompileSubjectKind,
} from '@/services/campaigns/interview-compile-service'
import { FileText, Loader2, Play, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useOrgAblyChannel } from '@/hooks/useOrgAblyChannel'
import { interviewCoachApi, resolveGoldenCompileTarget } from '@/services/interview-coach/api'
import type { LiveInterviewSessionSummary } from '@/services/interview-coach/types'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { subjectFeaturePath, subjectScope } from '@/modules/subjects/scope-navigation'
import {
    interviewCompileEditableFields,
    interviewCompileEditableCopy,
    interviewCompileMissingRequiredFields,
    interviewCompileReviewEntries,
    interviewCompileReviewShapeLabel,
    interviewCompileReviewValue,
    interviewCompileWithEditedFields,
} from './interview-compile-review.util'
import { heftAuthorityActionLabel } from '@/lib/heft-authority-language'

const ENABLED_FAMILIES: Record<InterviewCompileProfileType, string> = {
    heft: 'Stories, star points, facts, quotes, beacons, ethos, goals, and identity references',
    foundation: 'Goals, audience, TFD, ALO, claims, and proof references',
    identity: 'Identity profile dimensions and supporting profile references',
}

export function ProcessInterviewControl({
    targetProfileId,
    targetProfileType,
    targetSubjectKind,
    profileName,
    initialSourceMode = 'document',
    open,
    onOpenChange,
    showTrigger = true,
    showStatus,
    onApplyComplete,
}: {
    targetProfileId: string
    targetProfileType: InterviewCompileProfileType
    targetSubjectKind?: InterviewCompileSubjectKind
    profileName?: string | null
    initialSourceMode?: 'document' | 'paste' | 'session'
    open?: boolean
    onOpenChange?: (open: boolean) => void
    showTrigger?: boolean
    showStatus?: boolean
    onApplyComplete?: () => void | Promise<void>
}) {
    const [internalOpen, setInternalOpen] = useState(false)
    const dialogOpen = open ?? internalOpen
    const { subjectType, subjectId } = useParams<{ subjectType?: string; subjectId?: string }>()
    const navigate = useNavigate()
    const routeScope = subjectScope(subjectType, subjectId)
    const knowledgeReviewPath = routeScope
        ? subjectFeaturePath(routeScope, 'knowledge-review')
        : '/dashboard/knowledge-review'
    const setDialogOpen = onOpenChange ?? setInternalOpen
    const [pickerOpen, setPickerOpen] = useState(false)
    const [sourceMode, setSourceMode] = useState<'document' | 'paste' | 'session'>(initialSourceMode)
    const [document, setDocument] = useState<{
        id: string
        name: string
        updatedAt?: string
        size?: number
        preview?: string
    } | null>(null)
    const [session, setSession] = useState<LiveInterviewSessionSummary | null>(null)
    const [sessions, setSessions] = useState<LiveInterviewSessionSummary[]>([])
    const [sessionsLoading, setSessionsLoading] = useState(false)
    const [paste, setPaste] = useState('')
    const [job, setJob] = useState<InterviewCompileJob | null>(null)
    const [candidates, setCandidates] = useState<InterviewCompileCandidate[]>([])
    const [busy, setBusy] = useState(false)
    const [startError, setStartError] = useState<string | null>(null)
    const [startErrorReprocess, setStartErrorReprocess] = useState(false)
    const [cancelBusy, setCancelBusy] = useState(false)
    const [reviewBusyId, setReviewBusyId] = useState<string | null>(null)
    const [applyBusy, setApplyBusy] = useState(false)
    const [applyQueueError, setApplyQueueError] = useState<string | null>(null)
    const [applyRun, setApplyRun] = useState<InterviewApplyRun | null>(null)
    const [reviewFilter, setReviewFilter] = useState<'all' | InterviewCompileCandidate['reviewStatus']>('all')
    const [dispositionFilter, setDispositionFilter] = useState<'all' | InterviewCompileCandidate['disposition']>('all')
    const [reviewSearch, setReviewSearch] = useState('')
    const [selectedCandidateIds, setSelectedCandidateIds] = useState<Set<string>>(new Set())
    const [reprocessAfterReset, setReprocessAfterReset] = useState(false)
    const [editingCandidateId, setEditingCandidateId] = useState<string | null>(null)
    const [editCopy, setEditCopy] = useState('')
    const [editFields, setEditFields] = useState<Record<string, string>>({})
    const refreshedApplyRunId = useRef<string | null>(null)

    useEffect(() => {
        setSourceMode(initialSourceMode)
        setDocument(null)
        setSession(null)
        setPaste('')
    }, [initialSourceMode])

    const refreshProfileAfterApply = useCallback(
        (run: InterviewApplyRun) => {
            if (!['completed', 'partially_failed'].includes(run.status)) return
            if (refreshedApplyRunId.current === run.applyRunId) return
            refreshedApplyRunId.current = run.applyRunId
            void onApplyComplete?.()
        },
        [onApplyComplete]
    )

    const finishSuccessfulApply = useCallback(
        (run: InterviewApplyRun) => {
            refreshProfileAfterApply(run)
            if (run.status !== 'completed' || !job) return

            // Applying accepted candidates is only one step in review. Reconcile
            // the queue before deciding whether the review itself is complete;
            // otherwise a partial apply clears the still-pending candidates.
            void interviewCompileService
                .listCandidates(job.jobId)
                .then(result => {
                    setCandidates(result.candidates)
                    setApplyRun(null)
                    setSelectedCandidateIds(new Set())
                })
                .catch(() => toast.error('Profile updated, but review candidates could not be refreshed'))
        },
        [job, refreshProfileAfterApply]
    )

    useOrgAblyChannel('interview-compile.updated', message => {
        const update = message.data as {
            jobId?: string
            status?: InterviewCompileJob['status']
            stage?: string
            progress?: InterviewCompileJob['progress']
        }
        if (!update?.jobId || !job || update.jobId !== job.jobId) return

        setJob(current =>
            current && current.jobId === update.jobId
                ? {
                      ...current,
                      status: update.status ?? current.status,
                      stage: update.stage ?? current.stage,
                      progress: update.progress ?? current.progress,
                  }
                : current
        )

        if (update.status === 'review_ready') {
            void Promise.all([
                interviewCompileService.getJob(update.jobId),
                interviewCompileService.listCandidates(update.jobId),
            ])
                .then(([jobResult, candidateResult]) => {
                    setJob(jobResult.job)
                    setCandidates(candidateResult.candidates)
                })
                .catch(() => toast.error('Processing finished, but review candidates could not be loaded'))
        }
    })

    useOrgAblyChannel('interview-compile.apply.updated', message => {
        const update = message.data as { applyRunId?: string; status?: InterviewApplyRun['status'] }
        if (!update?.applyRunId || !applyRun || update.applyRunId !== applyRun.applyRunId) return
        setApplyRun(current => (current ? { ...current, status: update.status ?? current.status } : current))
        void interviewCompileService.getApplyRun(update.applyRunId).then(result => {
            setApplyRun(result.run)
            if (['completed', 'partially_failed', 'failed'].includes(result.run.status)) {
                if (result.run.status === 'completed') {
                    finishSuccessfulApply(result.run)
                } else {
                    refreshProfileAfterApply(result.run)
                    void interviewCompileService
                        .listCandidates(job!.jobId)
                        .then(candidateResult => setCandidates(candidateResult.candidates))
                }
            }
        })
    })

    const active = job?.status === 'queued' || job?.status === 'running'
    const canStart =
        sourceMode === 'document'
            ? Boolean(document)
            : sourceMode === 'session'
              ? Boolean(session)
              : paste.trim().length > 0
    const failedApplyErrors = useMemo(
        () =>
            new Map(
                (applyRun?.receipts ?? [])
                    .filter(receipt => receipt.status === 'failed' && receipt.error)
                    .map(receipt => [receipt.candidateId, receipt.error as string])
            ),
        [applyRun]
    )
    const failedCandidates = useMemo(
        () => candidates.filter(candidate => candidate.applyStatus === 'failed'),
        [candidates]
    )
    const remainingCandidates = useMemo(
        () =>
            candidates.filter(
                candidate =>
                    candidate.reviewStatus !== 'rejected' &&
                    candidate.applyStatus !== 'applied' &&
                    candidate.applyStatus !== 'queued'
            ),
        [candidates]
    )
    const pendingCandidates = useMemo(
        () => candidates.filter(candidate => candidate.reviewStatus === 'pending'),
        [candidates]
    )
    const acceptedAwaitingApply = useMemo(
        () =>
            candidates.filter(
                candidate =>
                    candidate.reviewStatus === 'accepted' &&
                    candidate.applyStatus !== 'applied' &&
                    candidate.applyStatus !== 'queued'
            ),
        [candidates]
    )
    const applyingCandidates = useMemo(
        () => candidates.filter(candidate => candidate.applyStatus === 'queued'),
        [candidates]
    )
    const allCandidatesReviewed = candidates.length > 0 && pendingCandidates.length === 0
    const reviewAwaitingApply =
        job?.status === 'review_ready' &&
        allCandidatesReviewed &&
        acceptedAwaitingApply.length > 0 &&
        failedCandidates.length === 0
    const reviewComplete =
        job?.status === 'review_ready' &&
        allCandidatesReviewed &&
        acceptedAwaitingApply.length === 0 &&
        applyingCandidates.length === 0 &&
        failedCandidates.length === 0

    const statusLabel = useMemo(() => {
        if (!job) return null
        if (job.status === 'review_ready') {
            if (failedCandidates.length) return `${failedCandidates.length} apply failed — review required`
            if (reviewAwaitingApply) return `${acceptedAwaitingApply.length} accepted candidates ready to apply`
            if (applyingCandidates.length) return `${applyingCandidates.length} candidate(s) applying to profile…`
            if (!remainingCandidates.length) return 'No candidates remaining'
            return `${remainingCandidates.length} candidates ready for review`
        }
        if (job.status === 'partially_failed')
            return `${remainingCandidates.length} candidates recovered; processing needs attention`
        if (job.status === 'failed') return 'Processing needs attention'
        return job.progress?.label ?? 'Processing interview…'
    }, [acceptedAwaitingApply.length, applyingCandidates.length, failedCandidates.length, job, remainingCandidates.length, reviewAwaitingApply])

    const visibleCandidates = useMemo(() => {
        const query = reviewSearch.trim().toLowerCase()
        return candidates.filter(candidate => {
            if (
                reviewFilter === 'all' &&
                (candidate.reviewStatus === 'rejected' ||
                    (candidate.reviewStatus === 'accepted' && candidate.applyStatus !== 'failed'))
            ) {
                return false
            }
            if (reviewFilter !== 'all' && candidate.reviewStatus !== reviewFilter) return false
            if (dispositionFilter !== 'all' && candidate.disposition !== dispositionFilter) return false
            if (!query) return true
            const value = candidate.currentValue as any
            const statement = String(value?.canonicalStatement ?? value?.displayDraft?.primaryStatement ?? '')
            const existing = (candidate.matchedExistingValues ?? []).map(match => match.text).join(' ')
            return `${candidate.objectType} ${candidate.disposition} ${statement} ${existing}`
                .toLowerCase()
                .includes(query)
        })
    }, [candidates, dispositionFilter, reviewFilter, reviewSearch])

    const selectVisibleCandidates = useCallback(() => {
        setSelectedCandidateIds(new Set(visibleCandidates.map(candidate => candidate.candidateId)))
    }, [visibleCandidates])

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            try {
            const result = await interviewCompileService.listActive(targetProfileId, {
                profileType: targetProfileType,
                subjectKind: targetSubjectKind,
            })
                if (!cancelled && result.jobs[0]) setJob(result.jobs[0])
            } catch {
                // Status recovery is best effort; the explicit control remains usable.
            }
        }
        void load()
        return () => {
            cancelled = true
        }
    }, [targetProfileId, targetProfileType, targetSubjectKind])

    useEffect(() => {
        if (!dialogOpen || sourceMode !== 'session') return
        let cancelled = false
        setSessionsLoading(true)
        void interviewCoachApi
            .listSessions(
                targetProfileType === 'heft'
                    ? { libraryEntityId: targetProfileId, limit: 50 }
                    : { profileId: targetProfileId, limit: 50 }
            )
            .then(result => {
                if (!cancelled) setSessions(result)
            })
            .catch(() => {
                if (!cancelled) toast.error('Could not load interview sessions')
            })
            .finally(() => {
                if (!cancelled) setSessionsLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [dialogOpen, sourceMode, targetProfileId, targetProfileType])

    useEffect(() => {
        if (!job || !active) return
        const timer = window.setInterval(async () => {
            try {
                const result = await interviewCompileService.getJob(job.jobId)
                setJob(result.job)
            } catch {
                // The next poll or HTTP recovery will reconcile status.
            }
        }, 3000)
        return () => window.clearInterval(timer)
    }, [active, job])

    useEffect(() => {
        if (!job || (job.status !== 'review_ready' && job.status !== 'partially_failed')) return
        setDialogOpen(true)
        void interviewCompileService
            .listCandidates(job.jobId)
            .then(result => setCandidates(result.candidates))
            .catch(() => toast.error('Could not load review candidates'))
    }, [job, setDialogOpen])

    const applyRunId = applyRun?.applyRunId
    const applyRunStatus = applyRun?.status
    const displayStatusLabel = applyRunStatus === 'completed' ? 'Profile updated successfully' : statusLabel

    useEffect(() => {
        if (!applyRunId || !applyRunStatus || ['completed', 'partially_failed', 'failed'].includes(applyRunStatus))
            return
        const timer = window.setInterval(() => {
            void interviewCompileService
                .getApplyRun(applyRunId)
                .then(result => {
                    setApplyRun(result.run)
                    if (['completed', 'partially_failed', 'failed'].includes(result.run.status) && job) {
                        if (result.run.status === 'completed') {
                            finishSuccessfulApply(result.run)
                            return undefined
                        }
                        refreshProfileAfterApply(result.run)
                        return interviewCompileService.listCandidates(job.jobId).then(candidateResult => {
                            setCandidates(candidateResult.candidates)
                        })
                    }
                    return undefined
                })
                .catch(() => {})
        }, 2500)
        return () => window.clearInterval(timer)
    }, [applyRunId, applyRunStatus, finishSuccessfulApply, job, refreshProfileAfterApply])

    const resetFailedRun = () => {
        setReprocessAfterReset(true)
        setStartErrorReprocess(true)
        setStartError(null)
        setJob(null)
        setCandidates([])
        setApplyRun(null)
        setApplyQueueError(null)
        setSelectedCandidateIds(new Set())
        setReviewFilter('all')
        setDispositionFilter('all')
        setReviewSearch('')
        setDocument(null)
        setSession(null)
        setPaste('')
        setSourceMode('document')
    }

    const finishReview = () => {
        setCandidates([])
        setApplyRun(null)
        setApplyQueueError(null)
        setSelectedCandidateIds(new Set())
        setJob(null)
        setDocument(null)
        setSession(null)
        setPaste('')
        setSourceMode(initialSourceMode)
        setDialogOpen(false)
    }

    const start = async (reprocess = false) => {
        if (!canStart) return
        setStartError(null)
        setStartErrorReprocess(reprocess || reprocessAfterReset)
        setBusy(true)
        try {
            if (sourceMode === 'session') {
                const selectedSession = session
                if (!selectedSession) {
                    throw new Error('Select an interview before compiling Knowledge Review')
                }
                const target = resolveGoldenCompileTarget(selectedSession)
                if (!target) {
                    throw new Error('Attach the interview Subject before compiling Knowledge Review')
                }
                const hasPriorGoldenAttempt = Boolean(selectedSession.goldenCompile)
                await interviewCoachApi.buildGoldenUnderstanding(selectedSession.sessionId, {
                    ...target,
                    rebuild: reprocess || reprocessAfterReset || hasPriorGoldenAttempt,
                })
                setReprocessAfterReset(false)
                setDialogOpen(false)
                toast.success('Knowledge Review compilation queued')
                navigate(`/dashboard/interviews/${encodeURIComponent(selectedSession.sessionId)}`)
                return
            }
            const source =
                sourceMode === 'document'
                    ? { kind: 'document' as const, documentId: document!.id }
                    : sourceMode === 'session'
                      ? { kind: 'interview_session' as const, sessionId: session!.sessionId }
                      : { kind: 'paste' as const, text: paste, title: `${profileName || 'Interview'} transcript` }
            const result = await interviewCompileService.start({
                targetProfileId,
                targetProfileType,
                targetSubjectKind,
                source,
                reprocess: reprocess || reprocessAfterReset,
                deferProcessing: true,
            })
            setJob(result.job)
            setReprocessAfterReset(false)
            setDialogOpen(false)
            toast.success('Interview added to Knowledge Review. Process it there when ready.')
        } catch (error) {
            const status =
                (error as { response?: { status?: number }; status?: number })?.response?.status ??
                (error as { status?: number })?.status
            const permissionDenied = status === 401 || status === 403
            setStartErrorReprocess(reprocess || reprocessAfterReset)
            setStartError(
                permissionDenied
                    ? 'You do not have permission to queue interview processing in this workspace. The selected source was not changed.'
                    : job?.status === 'queued' || job?.status === 'running'
                    ? 'This profile already has interview processing in progress. Refresh status and retry when it is no longer active.'
                    : 'Interview processing could not be queued. The selected source was not changed. Retry when the service is available.'
            )
            toast.error(
                job?.status === 'queued' || job?.status === 'running'
                    ? 'This profile already has interview processing in progress'
                    : 'Could not start interview processing'
            )
        } finally {
            setBusy(false)
        }
    }

    const cancel = async () => {
        if (!job || !active) return
        setCancelBusy(true)
        try {
            const result = await interviewCompileService.cancel(job.jobId)
            setJob(result.job)
            toast.success('Interview processing cancelled')
        } catch {
            toast.error('Could not cancel interview processing')
        } finally {
            setCancelBusy(false)
        }
    }

    const beginEdit = (candidate: InterviewCompileCandidate) => {
        const value = (candidate.proposedValue ?? candidate.currentValue) as Record<string, unknown>
        const fields = interviewCompileEditableFields(candidate, value)
        setEditingCandidateId(candidate.candidateId)
        setEditFields(Object.fromEntries(fields.map(field => [field.key, field.value])))
        setEditCopy(fields[0]?.value ?? interviewCompileEditableCopy(candidate, value))
    }

    const cancelEdit = () => {
        setEditingCandidateId(null)
        setEditCopy('')
        setEditFields({})
    }

    const saveEdit = async (candidate: InterviewCompileCandidate) => {
        if (!job) return
        const fields = interviewCompileEditableFields(
            candidate,
            (candidate.proposedValue ?? candidate.currentValue) as Record<string, unknown>,
        )
        const edits = { ...editFields, [fields[0]?.key ?? 'canonicalStatement']: editCopy }
        const missingRequired = fields.find(field => field.required && !String(edits[field.key] ?? '').trim())
        if (missingRequired) {
            toast.error(`${missingRequired.label} cannot be empty`)
            return
        }
        setReviewBusyId(candidate.candidateId)
        try {
            const value = interviewCompileWithEditedFields(
                candidate,
                (candidate.proposedValue ?? candidate.currentValue) as Record<string, unknown>,
                edits
            )
            const result = await interviewCompileService.reviewCandidate({
                jobId: job.jobId,
                candidateId: candidate.candidateId,
                action: 'edit',
                expectedRevision: candidate.revision,
                currentValue: value,
            })
            setCandidates(current =>
                current.map(item => (item.candidateId === candidate.candidateId ? result.candidate : item))
            )
            cancelEdit()
            toast.success('Candidate copy updated')
        } catch (error: any) {
            toast.error(
                error?.response?.status === 409
                    ? 'This candidate changed; refresh review.'
                    : 'Could not save candidate edit'
            )
        } finally {
            setReviewBusyId(null)
        }
    }

    const queueAcceptedCandidates = useCallback(
        async (accepted: InterviewCompileCandidate[]) => {
            if (!job || !accepted.length) return false

            setApplyQueueError(null)
            setApplyBusy(true)
            try {
                const candidateIds = accepted.map(candidate => candidate.candidateId)
                const result = await interviewCompileService.apply(
                    job.jobId,
                    candidateIds,
                    Object.fromEntries(accepted.map(candidate => [candidate.candidateId, candidate.revision]))
                )

                if (result.run) {
                    setApplyRun(result.run)
                    setCandidates(current =>
                        current.map(candidate =>
                            candidateIds.includes(candidate.candidateId)
                                ? { ...candidate, applyStatus: 'queued' }
                                : candidate
                        )
                    )
                    if (result.run.status === 'completed') finishSuccessfulApply(result.run)
                }

                toast.success(
                    `${accepted.length} ${accepted.length === 1 ? 'candidate' : 'candidates'} accepted and applying in the background`
                )
                return true
            } catch (error) {
                const status =
                    (error as { response?: { status?: number }; status?: number })?.response?.status ??
                    (error as { status?: number })?.status
                setApplyQueueError(
                    status === 401 || status === 403
                        ? 'You do not have permission to apply reviewed interview data in this workspace. The accepted review decision was preserved.'
                        : 'Accepted review decisions were preserved, but the Profile apply could not be queued. Retry when the service is available.'
                )
                toast.error('Accepted, but profile apply could not be queued. Retry apply from the Accepted filter.')
                return false
            } finally {
                setApplyBusy(false)
            }
        },
        [finishSuccessfulApply, job]
    )

    const decide = async (candidate: InterviewCompileCandidate, action: 'accept' | 'reject') => {
        if (action === 'accept') {
            const missingRequiredFields = interviewCompileMissingRequiredFields(
                candidate,
                (candidate.proposedValue ?? candidate.currentValue) as Record<string, unknown>,
            )
            if (missingRequiredFields.length) {
                toast.error(`Complete ${missingRequiredFields.join(', ')} before accepting`)
                return
            }
        }
        setReviewBusyId(candidate.candidateId)
        try {
            const result = await interviewCompileService.reviewCandidate({
                jobId: job!.jobId,
                candidateId: candidate.candidateId,
                action,
                expectedRevision: candidate.revision,
                reviewStatus: action === 'accept' ? 'accepted' : 'rejected',
            })
            setCandidates(current =>
                current.map(item => (item.candidateId === candidate.candidateId ? result.candidate : item))
            )
            if (action === 'accept') await queueAcceptedCandidates([result.candidate])
        } catch (error: any) {
            toast.error(
                error?.response?.status === 409
                    ? 'This candidate changed; refresh the review.'
                    : 'Could not save review decision'
            )
        } finally {
            setReviewBusyId(null)
        }
    }

    const decideSelected = async (action: 'accept' | 'reject') => {
        const selected = candidates.filter(candidate => selectedCandidateIds.has(candidate.candidateId))
        if (!selected.length || !job) return
        if (action === 'accept') {
            const incomplete = selected.flatMap(candidate =>
                interviewCompileMissingRequiredFields(
                    candidate,
                    (candidate.proposedValue ?? candidate.currentValue) as Record<string, unknown>,
                ).map(field => `${candidate.objectType}: ${field}`),
            )
            if (incomplete.length) {
                toast.error(`Complete shape fields before accepting: ${incomplete.join(', ')}`)
                return
            }
        }
        setReviewBusyId('__bulk__')
        try {
            const results = await Promise.all(
                selected.map(candidate =>
                    interviewCompileService.reviewCandidate({
                        jobId: job.jobId,
                        candidateId: candidate.candidateId,
                        action,
                        expectedRevision: candidate.revision,
                        reviewStatus: action === 'accept' ? 'accepted' : 'rejected',
                    })
                )
            )
            const updatedById = new Map(results.map(result => [result.candidate.candidateId, result.candidate]))
            setCandidates(current => current.map(candidate => updatedById.get(candidate.candidateId) ?? candidate))
            setSelectedCandidateIds(new Set())
            if (action === 'accept') await queueAcceptedCandidates(results.map(result => result.candidate))
        } catch {
            toast.error('Some selected candidates changed; refresh and retry the bulk action')
        } finally {
            setReviewBusyId(null)
        }
    }

    const applyAccepted = async () => {
        const accepted = candidates.filter(
            candidate =>
                candidate.reviewStatus === 'accepted' &&
                candidate.applyStatus !== 'applied' &&
                candidate.applyStatus !== 'queued'
        )
        await queueAcceptedCandidates(accepted)
    }

    const undo = async (candidate: InterviewCompileCandidate) => {
        setReviewBusyId(candidate.candidateId)
        try {
            const result = await interviewCompileService.undoCandidate(job!.jobId, candidate.candidateId)
            setCandidates(current =>
                current.map(item => (item.candidateId === candidate.candidateId ? result.candidate : item))
            )
        } catch {
            toast.error('Could not undo the latest decision')
        } finally {
            setReviewBusyId(null)
        }
    }

    return (
        <>
            {showStatus && job && displayStatusLabel ? (
                <button
                    type='button'
                    onClick={() => setDialogOpen(true)}
                    className={`inline-flex max-w-[220px] items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                        applyRunStatus === 'completed'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : job.status === 'failed' || job.status === 'partially_failed'
                              ? 'border-amber-200 bg-amber-50 text-amber-800'
                              : job.status === 'review_ready'
                                ? 'border-violet-200 bg-violet-50 text-violet-700'
                                : 'border-sky-200 bg-sky-50 text-sky-700'
                    }`}
                    title='Open interview processing status'
                    aria-label={`Interview status: ${displayStatusLabel}`}
                >
                    {active ? <Loader2 className='h-3 w-3 animate-spin' /> : null}
                    <span className='truncate'>{displayStatusLabel}</span>
                </button>
            ) : null}

                {job?.status === 'review_ready' || job?.status === 'queued' ? (
                <Link
                    to={knowledgeReviewPath}
                    className='inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-100'
                >
                    {job.status === 'queued' ? 'Process in Knowledge Review' : 'Open Knowledge Review'}
                </Link>
            ) : null}

            {showTrigger ? (
                <div className='flex items-center gap-2'>
                    {job && displayStatusLabel ? (
                        <span className='hidden text-[11px] text-slate-500 sm:inline-flex' title={displayStatusLabel}>
                            {active ? <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' /> : null}
                            {displayStatusLabel}
                        </span>
                    ) : null}
                    <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        className='h-7 gap-1 text-xs'
                        onClick={() => setDialogOpen(true)}
                    >
                        {active ? <RefreshCw className='h-3.5 w-3.5' /> : <Play className='h-3.5 w-3.5' />}
                        Process Interview
                    </Button>
                </div>
            ) : null}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent
                    className='flex h-[min(88dvh,820px)] w-[94vw] max-w-[1100px] resize flex-col overflow-hidden'
                    title='Drag the lower-right corner to resize this review window'
                >
                    <DialogHeader>
                        <DialogTitle>Process Interview</DialogTitle>
                        <DialogDescription>
                            Harvest {targetProfileType} data for {profileName || 'this profile'} using the governed
                            compiler. Review is required before anything is applied.
                            <span className='mt-1 block text-[11px] text-slate-500'>
                                Enabled data families: {ENABLED_FAMILIES[targetProfileType]}
                            </span>
                        </DialogDescription>
                        <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            className='w-fit border-slate-200 text-slate-700 hover:bg-slate-50'
                            aria-label='Reset interview'
                            onClick={resetFailedRun}
                        >
                            Reset
                        </Button>
                    </DialogHeader>
                    {job?.status === 'review_ready' || job?.status === 'partially_failed' ? (
                        <div className='flex min-h-0 flex-1 flex-col space-y-3 overflow-hidden'>
                {job.status === 'partially_failed' ? (
                    <div
                                    role='alert'
                                    className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900'
                                >
                                    Processing stopped after some candidates were saved. Review and apply the recovered
                                    candidates below; the failed portion remains retryable.
                    </div>
                ) : null}
                {reviewAwaitingApply ? (
                    <div
                        role='status'
                        className='rounded-lg border border-violet-200 bg-violet-50 px-3 py-3 text-sm text-violet-900'
                    >
                        <p className='font-semibold'>Review complete — apply accepted candidates</p>
                        <p className='mt-0.5 text-xs text-violet-800'>
                            Every candidate has a decision. Click {heftAuthorityActionLabel('applied', 'actionBatch')} to save the{' '}
                            {acceptedAwaitingApply.length === 1 ? 'accepted candidate' : 'accepted candidates'} to the
                            profile.
                        </p>
                    </div>
                ) : null}
                {applyQueueError ? (
                    <div
                        role='alert'
                        className='rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900'
                    >
                        <p className='font-semibold'>Profile apply could not be queued.</p>
                        <p className='mt-1'>{applyQueueError}</p>
                        <Button
                            type='button'
                            size='sm'
                            variant='outline'
                            className='mt-2 border-rose-300 text-rose-800 hover:bg-rose-100'
                            disabled={applyBusy || acceptedAwaitingApply.length === 0}
                            onClick={() => void applyAccepted()}
                        >
                            Retry apply
                        </Button>
                    </div>
                ) : null}

                {reviewComplete ? (
                    <div
                        role='status'
                        className='flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900'
                    >
                        <div>
                            <p className='font-semibold'>Review complete</p>
                            <p className='mt-0.5 text-xs text-emerald-800'>
                                Every candidate has been handled. No further action is required.
                            </p>
                        </div>
                        <Button type='button' size='sm' onClick={finishReview}>
                            Done
                        </Button>
                    </div>
                ) : null}
                <div className='flex items-center justify-between gap-3'>
                                <p className='text-xs text-slate-600'>
                                    Review candidates before acceptance; accepted candidates apply to the profile in the background.
                                </p>
                                <div className='flex flex-wrap justify-end gap-1'>
                                    <Button
                                        type='button'
                                        size='sm'
                                        variant='ghost'
                                        disabled={!visibleCandidates.length || reviewBusyId === '__bulk__'}
                                        onClick={selectVisibleCandidates}
                                    >
                                        Select all visible
                                    </Button>
                                    <Button
                                        type='button'
                                        size='sm'
                                        variant='ghost'
                                        disabled={!selectedCandidateIds.size || reviewBusyId === '__bulk__'}
                                        onClick={() => setSelectedCandidateIds(new Set())}
                                    >
                                        Clear selection
                                    </Button>
                                    <Button
                                        type='button'
                                        size='sm'
                                        variant='outline'
                                        disabled={reviewBusyId === '__bulk__' || !selectedCandidateIds.size}
                                        onClick={() => void decideSelected('reject')}
                                    >
                                        Reject selected
                                    </Button>
                                    <Button
                                        type='button'
                                        size='sm'
                                        variant='outline'
                                        disabled={reviewBusyId === '__bulk__' || !selectedCandidateIds.size}
                                        onClick={() => void decideSelected('accept')}
                                    >
                                        {heftAuthorityActionLabel('accepted', 'actionBatch')}
                                    </Button>
                                    <Button
                                        type='button'
                                        size='sm'
                        disabled={
                            applyBusy ||
                            acceptedAwaitingApply.length === 0
                        }
                                        onClick={() => void applyAccepted()}
                                    >
                                        {applyBusy ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
                                        {heftAuthorityActionLabel('applied', 'actionBatch')}
                                    </Button>
                                </div>
                            </div>
                            {applyRun ? (
                                <p className='text-xs text-slate-500'>
                                    Apply status: <span className='font-semibold'>{applyRun.status}</span>
                                    {applyRun.counts
                                        ? ` · ${applyRun.counts.applied} applied · ${applyRun.counts.failed} failed · ${applyRun.counts.skipped} skipped · ${applyRun.counts.created} new · ${applyRun.counts.updated} updated · ${applyRun.counts.duplicate} duplicate`
                                        : applyRun.receipts.length
                                          ? ` · ${applyRun.receipts.length} result(s)`
                                          : ''}
                                </p>
                            ) : null}
                            {failedCandidates.length ? (
                                <div
                                    role='alert'
                                    className='rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900'
                                >
                                    <p className='font-semibold'>
                                        {failedCandidates.length} candidate apply{' '}
                                        {failedCandidates.length === 1 ? 'failed' : 'fail'}.
                                    </p>
                                    <ul className='mt-1 space-y-1'>
                                        {failedCandidates.map(candidate => (
                                            <li key={candidate.candidateId}>
                                                {candidate.objectType}:{' '}
                                                {candidate.applyError ??
                                                    failedApplyErrors.get(candidate.candidateId) ??
                                                    'Apply failed'}
                                            </li>
                                        ))}
                                    </ul>
                                    <p className='mt-1'>
                                        Edit the proposed copy if needed, then apply accepted again to retry the failed
                                        candidate.
                                    </p>
                                </div>
                            ) : null}
                            <div className='flex flex-wrap gap-2'>
                                <input
                                    value={reviewSearch}
                                    onChange={event => setReviewSearch(event.target.value)}
                                    placeholder='Search candidates…'
                                    className='min-w-0 flex-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs outline-none focus:border-violet-400'
                                />
                                <select
                                    value={reviewFilter}
                                    onChange={event =>
                                        setReviewFilter(
                                            event.target.value as 'all' | InterviewCompileCandidate['reviewStatus']
                                        )
                                    }
                                    className='rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600'
                                >
                                    <option value='all'>Active review</option>
                                    <option value='pending'>Pending</option>
                                    <option value='accepted'>Accepted archive</option>
                                    <option value='rejected'>Rejected</option>
                                </select>
                                <select
                                    value={dispositionFilter}
                                    onChange={event =>
                                        setDispositionFilter(
                                            event.target.value as 'all' | InterviewCompileCandidate['disposition']
                                        )
                                    }
                                    className='rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600'
                                >
                                    <option value='all'>All dispositions</option>
                                    <option value='new'>New</option>
                                    <option value='update'>Update</option>
                                    <option value='duplicate'>Duplicate</option>
                                    <option value='conflict'>Conflict</option>
                                    <option value='related'>Related</option>
                                </select>
                            </div>
                            <div
                                className='flex items-center justify-between gap-2 text-[11px] text-slate-500'
                            >
                                <span>
                                    Showing {visibleCandidates.length} of {remainingCandidates.length} active
                                    candidates. Compare the proposed harvest with the current profile before accepting.
                                    {candidates.some(candidate => candidate.reviewStatus === 'accepted')
                                        ? ` · ${candidates.filter(candidate => candidate.reviewStatus === 'accepted').length} accepted archived`
                                        : ''}
                                    {selectedCandidateIds.size ? ` · ${selectedCandidateIds.size} selected` : ''}
                                </span>
                            </div>
                            <div className='min-h-0 flex-1 space-y-2 overflow-y-auto pr-1'>
                                {visibleCandidates.map(candidate => {
                                    const value = (candidate.proposedValue ?? candidate.currentValue) as any
                                    const currentProfileValue = candidate.currentProfileValue
                                    const statement = String(
                                        value?.canonicalStatement ??
                                            value?.displayDraft?.primaryStatement ??
                                            candidate.objectType
                                    )
                                    const proposedEntries = interviewCompileReviewEntries(value, candidate)
                                    const currentEntries = interviewCompileReviewEntries(currentProfileValue, candidate)
                                    const editing = editingCandidateId === candidate.candidateId
                                    const editableFields = editing
                                        ? interviewCompileEditableFields(candidate, value)
                                        : []
                                    const missingRequiredFields = interviewCompileMissingRequiredFields(candidate, value)
                                    const metadataEntries = interviewCompileReviewEntries(value)
                                    const shapeLabel = interviewCompileReviewShapeLabel(candidate)
                                    const applyError =
                                        candidate.applyError ?? failedApplyErrors.get(candidate.candidateId)
                                    const candidateBusy = reviewBusyId === candidate.candidateId
                                    return (
                                        <div
                                            key={candidate.candidateId}
                                            className='rounded-lg border border-slate-200 bg-white p-3'
                                        >
                                            <div className='flex items-start justify-between gap-3'>
                                                <input
                                                    type='checkbox'
                                                    checked={selectedCandidateIds.has(candidate.candidateId)}
                                                    onChange={() =>
                                                        setSelectedCandidateIds(current => {
                                                            const next = new Set(current)
                                                            if (next.has(candidate.candidateId))
                                                                next.delete(candidate.candidateId)
                                                            else next.add(candidate.candidateId)
                                                            return next
                                                        })
                                                    }
                                                    aria-label={`Select ${candidate.objectType} candidate`}
                                                    className='mt-1 h-3.5 w-3.5 shrink-0 accent-violet-600'
                                                />
                                                <div className='min-w-0'>
                                                    <div className='mb-1 flex flex-wrap items-center gap-1.5'>
                                                        <span className='text-sm font-bold uppercase tracking-wide text-slate-500'>
                                                            {shapeLabel}
                                                            <span className='text-xs font-normal normal-case text-slate-400'>
                                                                ({candidate.objectType})
                                                            </span>
                                                        </span>
                                                        <span className='rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600'>
                                                            {candidate.disposition}
                                                        </span>
                                                        <span className='rounded-full bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800'>
                                                            {candidate.reviewStatus}
                                                        </span>
                                                        {candidate.applyStatus === 'failed' ? (
                                                            <span className='rounded-full bg-rose-100 px-1.5 py-0.5 text-xs font-semibold text-rose-800'>
                                                                apply failed
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    {candidate.applyStatus === 'failed' ? (
                                                        <p className='mb-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs leading-relaxed text-rose-900'>
                                                            <span className='font-semibold'>Apply failed:</span>{' '}
                                                            {applyError ?? 'The apply worker did not provide a reason.'}
                                                        </p>
                                                    ) : null}
                                                    {candidate.destinationSnapshot?.destinationKeys?.length ? (
                                                        <p className='mb-1 text-[11px] text-slate-500'>
                                                            Destination:{' '}
                                                            {candidate.destinationSnapshot.destinationKeys.join(', ')}
                                                        </p>
                                                    ) : null}
                                                    <div className='grid gap-2 md:grid-cols-2'>
                                                        <div className='rounded-md border border-violet-100 bg-violet-50/50 px-2.5 py-2'>
                                                            <div className='flex items-center justify-between gap-2'>
                                                                <div className='text-[10px] font-bold uppercase tracking-wide text-violet-700'>
                                                                    Proposed harvest
                                                                </div>
                                                                {!editing ? (
                                                                    <Button
                                                                        type='button'
                                                                        size='sm'
                                                                        variant='ghost'
                                                                        className='h-6 px-2 text-xs text-violet-700'
                                                                        disabled={candidateBusy}
                                                                        onClick={() => beginEdit(candidate)}
                                                                    >
                                                                        Edit copy
                                                                    </Button>
                                                                ) : null}
                                                            </div>
                                                            <p className='mt-1 text-sm leading-relaxed text-slate-800'>
                                                                {statement}
                                                            </p>
                                                            {proposedEntries.length ? (
                                                                <dl className='mt-2 space-y-1 border-t border-violet-100 pt-2 text-xs'>
                                                                    {proposedEntries.map(entry => (
                                                                        <div key={entry.key}>
                                                                            <dt className='font-semibold text-slate-600'>
                                                                                {entry.label}
                                                                            </dt>
                                                                            <dd className='whitespace-pre-wrap break-words text-slate-700'>
                                                                                {interviewCompileReviewValue(
                                                                                    entry.value
                                                                                )}
                                                                            </dd>
                                                                        </div>
                                                                    ))}
                                                                </dl>
                                                            ) : null}
                                                            {metadataEntries.length ? (
                                                                <details className='mt-2 border-t border-violet-100 pt-2 text-xs text-slate-500'>
                                                                    <summary className='cursor-pointer font-semibold text-slate-600'>
                                                                        Compiler metadata
                                                                    </summary>
                                                                    <dl className='mt-2 space-y-1'>
                                                                        {metadataEntries.map(entry => (
                                                                            <div key={`metadata-${entry.key}`}>
                                                                                <dt className='font-semibold text-slate-500'>
                                                                                    {entry.label}
                                                                                </dt>
                                                                                <dd className='whitespace-pre-wrap break-words text-slate-600'>
                                                                                    {interviewCompileReviewValue(entry.value)}
                                                                                </dd>
                                                                            </div>
                                                                        ))}
                                                                    </dl>
                                                                </details>
                                                            ) : null}
                                                            {missingRequiredFields.length ? (
                                                                <div
                                                                    role='alert'
                                                                    className='mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs leading-relaxed text-amber-900'
                                                                >
                                                                    <span className='font-semibold'>Needs shape fields before acceptance:</span>{' '}
                                                                    {missingRequiredFields.join(', ')}
                                                                </div>
                                                            ) : null}
                                                            {editing ? (
                                                                <div className='mt-3 border-t border-violet-100 pt-3'>
                                                                    <div className='space-y-2'>
                                                                        {editableFields.map((field, index) => {
                                                                            const fieldId = `edit-${field.key}-${candidate.candidateId}`
                                                                            const fieldValue = index === 0 ? editCopy : editFields[field.key] ?? field.value
                                                                            return (
                                                                                <div key={field.key}>
                                                                                    <label
                                                                                        htmlFor={fieldId}
                                                                                        className='text-xs font-semibold text-slate-600'
                                                                                    >
                                                                                        {field.label}
                                                                                        {field.required ? ' *' : ''}
                                                                                    </label>
                                                                                    <textarea
                                                                                        id={fieldId}
                                                                                        value={fieldValue}
                                                                                        onChange={event => {
                                                                                            const nextValue = event.target.value
                                                                                            if (index === 0) setEditCopy(nextValue)
                                                                                            setEditFields(current => ({
                                                                                                ...current,
                                                                                                [field.key]: nextValue,
                                                                                            }))
                                                                                        }}
                                                                                        rows={field.multiline ? 4 : 2}
                                                                                        className='mt-1 min-h-12 w-full resize-y rounded-md border border-violet-200 bg-white px-2.5 py-2 text-sm leading-relaxed text-slate-800 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100'
                                                                                        aria-label={
                                                                                            index === 0
                                                                                                ? `Edit ${shapeLabel} copy`
                                                                                                : `Edit ${shapeLabel} ${field.label}`
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                            )
                                                                        })}
                                                                    </div>
                                                                    <p className='mt-2 text-[11px] text-slate-500'>
                                                                        Edit the content fields that will be applied. Evidence,
                                                                        provenance, confidence, and destination metadata remain
                                                                        governed by the compiler.
                                                                    </p>
                                                                    <div className='mt-2 flex justify-end gap-2'>
                                                                        <Button
                                                                            type='button'
                                                                            size='sm'
                                                                            variant='ghost'
                                                                            disabled={candidateBusy}
                                                                            onClick={cancelEdit}
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                        <Button
                                                                            type='button'
                                                                            size='sm'
                                                                            disabled={candidateBusy}
                                                                            onClick={() => void saveEdit(candidate)}
                                                                        >
                                                                            {candidateBusy ? 'Saving…' : 'Save edit'}
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                        <div className='rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2'>
                                                            <div className='text-[10px] font-bold uppercase tracking-wide text-slate-600'>
                                                                Current profile comparison
                                                            </div>
                                                            {currentEntries.length ? (
                                                                <dl className='mt-1 space-y-1 text-xs'>
                                                                    {currentEntries.map(entry => (
                                                                        <div key={entry.key}>
                                                                            <dt className='font-semibold text-slate-600'>
                                                                                {entry.label}
                                                                            </dt>
                                                                            <dd className='whitespace-pre-wrap break-words text-slate-700'>
                                                                                {interviewCompileReviewValue(
                                                                                    entry.value
                                                                                )}
                                                                            </dd>
                                                                        </div>
                                                                    ))}
                                                                </dl>
                                                            ) : (
                                                                <p className='mt-1 text-xs text-slate-500'>
                                                                    No existing value in this profile slot.
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {candidate.matchedExistingValues?.length ? (
                                                        <div className='mt-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs'>
                                                            <div className='font-semibold text-slate-600'>
                                                                Current profile value
                                                            </div>
                                                            {candidate.matchedExistingValues.map(match => (
                                                                <p
                                                                    key={match.id}
                                                                    className='mt-0.5 leading-relaxed text-slate-600'
                                                                >
                                                                    {match.text}
                                                                </p>
                                                            ))}
                                                            <p className='mt-1 text-[10px] uppercase tracking-wide text-slate-400'>
                                                                {candidate.disposition === 'duplicate'
                                                                    ? 'Exact duplicate — do not apply unless the transcript adds provenance.'
                                                                    : 'Existing value found — review whether this is an update or a distinct item.'}
                                                            </p>
                                                        </div>
                                                    ) : null}
                                                    {candidate.sourceEvidenceRefs?.length ? (
                                                        <div className='mt-2 border-l-2 border-violet-200 pl-2 text-xs text-slate-500'>
                                                            <span className='font-semibold text-slate-600'>
                                                                Transcript evidence
                                                            </span>
                                                            {candidate.sourceEvidenceRefs
                                                                .slice(0, 2)
                                                                .map((evidence, index) => (
                                                                    <p
                                                                        key={`${evidence.evidenceId ?? 'evidence'}-${index}`}
                                                                        className='mt-1 italic'
                                                                    >
                                                                        “{evidence.excerpt}”
                                                                    </p>
                                                                ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className='flex shrink-0 gap-1'>
                                                    {candidate.reviewStatus !== 'pending' ? (
                                                        <Button
                                                            type='button'
                                                            size='sm'
                                                            variant='ghost'
                                                            disabled={candidateBusy}
                                                            onClick={() => void undo(candidate)}
                                                        >
                                                            Undo
                                                        </Button>
                                                    ) : null}
                                                    <Button
                                                        type='button'
                                                        size='sm'
                                                        variant='outline'
                                                        disabled={candidateBusy}
                                                        onClick={() => void decide(candidate, 'reject')}
                                                    >
                                                        Reject
                                                    </Button>
                                    <Button
                                        type='button'
                                        size='sm'
                                        disabled={candidateBusy || missingRequiredFields.length > 0}
                                        title={
                                            missingRequiredFields.length
                                                ? `Missing: ${missingRequiredFields.join(', ')}`
                                                : undefined
                                        }
                                        onClick={() => void decide(candidate, 'accept')}
                                    >
                                                        {heftAuthorityActionLabel('accepted', 'actionShort')}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                                {!visibleCandidates.length ? (
                                    <p className='py-8 text-center text-xs text-slate-500'>
                                        {candidates.some(candidate => candidate.reviewStatus !== 'accepted')
                                            ? 'No candidates match this filter.'
                                            : candidates.length
                                              ? 'Accepted candidates are archived.'
                                              : 'No candidates were produced.'}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto pr-1'>
                            <div className='flex gap-2'>
                                {(['document', 'session', 'paste'] as const).map(mode => (
                                    <button
                                        key={mode}
                                        type='button'
                                        className={`rounded-lg px-3 py-2 text-xs font-semibold ${sourceMode === mode ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
                                        onClick={() => setSourceMode(mode)}
                                    >
                                        {mode === 'document'
                                            ? 'Import document'
                                            : mode === 'session'
                                              ? 'Existing interview'
                                              : 'Paste transcript'}
                                    </button>
                                ))}
                            </div>
                            {sourceMode === 'document' ? (
                                <div className='rounded-lg border border-slate-200 p-3'>
                                    <div className='flex items-center justify-between gap-3'>
                                        <div className='flex min-w-0 items-center gap-2 text-sm text-slate-700'>
                                            <FileText className='h-4 w-4 shrink-0 text-violet-600' />
                                            <span className='min-w-0 truncate'>
                                                <span className='block truncate'>
                                                    {document?.name || 'No transcript selected'}
                                                </span>
                                                {document ? (
                                                    <span className='block text-[11px] text-slate-400'>
                                                        {document.size
                                                            ? `${document.size.toLocaleString()} bytes · `
                                                            : ''}
                                                        {document.updatedAt
                                                            ? `Updated ${new Date(document.updatedAt).toLocaleDateString()}`
                                                            : 'Selected from document library'}
                                                    </span>
                                                ) : null}
                                            </span>
                                        </div>
                                        <Button
                                            type='button'
                                            size='sm'
                                            variant='outline'
                                            onClick={() => setPickerOpen(true)}
                                        >
                                            Select transcript document
                                        </Button>
                                    </div>
                                    {document?.preview ? (
                                        <p className='mt-2 line-clamp-3 border-t border-slate-100 pt-2 text-xs leading-relaxed text-slate-500'>
                                            {document.preview}
                                        </p>
                                    ) : null}
                                </div>
                            ) : sourceMode === 'session' ? (
                                <div className='space-y-2 rounded-lg border border-slate-200 p-3'>
                                    {sessionsLoading ? (
                                        <div className='flex items-center gap-2 py-3 text-xs text-slate-500'>
                                            <Loader2 className='h-4 w-4 animate-spin' /> Loading interviews…
                                        </div>
                                    ) : sessions.length ? (
                                        sessions.map(item => (
                                            <button
                                                key={item.sessionId}
                                                type='button'
                                                onClick={() => setSession(item)}
                                                className={`w-full rounded-md border p-2 text-left text-xs ${session?.sessionId === item.sessionId ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'}`}
                                            >
                                                <span className='block font-semibold text-slate-700'>
                                                    {item.title || 'Untitled interview'}
                                                </span>
                                                <span className='text-slate-500'>
                                                    {item.turnCount} turns · Updated{' '}
                                                    {new Date(item.updatedAt).toLocaleDateString()}
                                                </span>
                                            </button>
                                        ))
                                    ) : (
                                        <p className='py-3 text-xs text-slate-500'>
                                            No saved interviews are available for this profile.
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <textarea
                                    value={paste}
                                    onChange={event => setPaste(event.target.value)}
                                    placeholder='Paste the interview transcript here…'
                                    className='min-h-48 w-full rounded-lg border border-slate-200 p-3 text-sm outline-none focus:border-violet-400'
                                />
                            )}
                            {startError ? (
                                <div
                                    role='alert'
                                    className='rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900'
                                >
                                    <p className='font-semibold'>Interview processing could not be queued.</p>
                                    <p className='mt-1'>{startError}</p>
                                    <Button
                                        type='button'
                                        size='sm'
                                        variant='outline'
                                        className='mt-2 border-rose-300 text-rose-800 hover:bg-rose-100'
                                        disabled={!canStart || busy}
                                        onClick={() => void start(startErrorReprocess)}
                                    >
                                        Retry interview processing
                                    </Button>
                                </div>
                            ) : null}
                            {job?.status === 'failed' ? (
                                <div
                                    role='alert'
                                    className='rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-900'
                                >
                                    Processing failed before review. Retry with the selected source or reset this run to
                                    choose a different transcript.
                                </div>
                            ) : null}
                            {job && statusLabel ? <p className='text-xs text-slate-500'>{statusLabel}</p> : null}
                            <div className='flex justify-end gap-2'>
                                <Button type='button' variant='ghost' onClick={() => setDialogOpen(false)}>
                                    Cancel
                                </Button>
                                {job?.status === 'failed' ? (
                                    <>
                                        <Button type='button' variant='ghost' onClick={resetFailedRun}>
                                            Reset
                                        </Button>
                                        <Button
                                            type='button'
                                            variant='outline'
                                            disabled={!canStart || busy}
                                            onClick={() => void start(true)}
                                        >
                                            Retry processing
                                        </Button>
                                    </>
                                ) : null}
                                {active ? (
                                    <Button
                                        type='button'
                                        variant='outline'
                                        disabled={cancelBusy || busy}
                                        onClick={() => void cancel()}
                                    >
                                        {cancelBusy ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
                                        Stop processing
                                    </Button>
                                ) : null}
                                <Button
                                    type='button'
                                    disabled={!canStart || busy || active}
                                    onClick={() => void start(startErrorReprocess || reprocessAfterReset)}
                                >
                                    {busy ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
                                    Queue processing
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <DocumentPickerModal
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                onSelect={doc =>
                    setDocument({
                        id: String(doc._id ?? doc.id),
                        name: doc.name || 'Untitled document',
                        updatedAt: doc.updatedAt,
                        size: doc.size,
                        preview:
                            String(doc.content || doc.description || '')
                                .trim()
                                .slice(0, 280) || undefined,
                    })
                }
                title='Select interview transcript'
                description='Choose the transcript document to send to the governed Process Interview compiler.'
            />
        </>
    )
}
